#### Hirer configuration queries

SEEK’s support team configures hirers to work with the SEEK API:

- [Hirer relationships] are added as part of onboarding hirers to an integration partner.

- [Link out applications] are enabled for hirers that comply with our link out terms of use.

Previously, integration partners haven’t had visibility into SEEK’s configuration.
This required manual reconciliation with our partners to avoid unexpected failures.

We’ve extended the SEEK API to support automatically detecting hirer configuration:

- The [`HiringOrganization`] object now has a `seekApiCapabilities` field.
  This allows you to query a hirer’s active relationships and allowed application methods.

  You can retrieve a `HiringOrganization` using either the [`hiringOrganization` query] or the [`seekAnzAdvertiser` query]:

  ```graphql label="By hirer ID"
  query ($id: String!) {
    hiringOrganization(id: $id) {
      name
      seekApiCapabilities {
        relationshipTypeCodes
        applicationMethodCodes
      }
    }
  }
  ```

  ```graphql label="By SEEK advertiser ID" lovato-ignore-no-deprecated-schema
  query ($id: Int!) {
    seekAnzAdvertiser(id: $id) {
      name
      seekApiCapabilities {
        relationshipTypeCodes
        applicationMethodCodes
      }
    }
  }
  ```

- The new [`hiringOrganizations` query] allows you to list all the SEEK hirers you have an active relationship with.
  You can optionally filter on the hirer’s name or specific relationship types.

  You can use this to retrieve a complete list of your SEEK hirers, including their identifiers and API capabilities:

  ```graphql
  query ($schemeId: String!, $first: Int) {
    hiringOrganizations(schemeId: $schemeId, first: $first) {
      edges {
        node {
          id {
            value
          }
          name
          seekApiCapabilities {
            relationshipTypeCodes
            applicationMethodCodes
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ```

  ```json
  {
    "schemeId": "seekAnzPublicTest",
    "first": 3
  }
  ```

  ```json
  {
    "hiringOrganizations": {
      "edges": [
        {
          "node": {
            "id": {
              "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
            },
            "name": "Acme Corp",
            "seekApiCapabilities": {
              "relationshipTypeCodes": [
                "ApplicationExport",
                "ApplicationPrefill",
                "CandidateProfiles",
                "CandidateVerification",
                "JobPosting",
                "ProactiveSourcing"
              ],
              "applicationMethodCodes": ["ApplicationUri"]
            }
          }
        },
        {
          "node": {
            "id": {
              "value": "seekAnzPublicTest:organization:seek:A3HL8E4w"
            },
            "name": "Contoso",
            "seekApiCapabilities": {
              "relationshipTypeCodes": [
                "ApplicationExport",
                "ApplicationPrefill"
              ],
              "applicationMethodCodes": ["ApplicationUri"]
            }
          }
        },
        {
          "node": {
            "id": {
              "value": "seekAnzPublicTest:organization:seek:AYAWCibZ"
            },
            "name": "Fabrikam",
            "seekApiCapabilities": {
              "relationshipTypeCodes": [
                "ApplicationExport",
                "ApplicationPrefill"
              ],
              "applicationMethodCodes": []
            }
          }
        }
      ],
      "pageInfo": {
        "hasNextPage": true,
        "endCursor": "seekAnzPublicTest:hiringOrganizationCursor:seek:AYAWCibZ"
      }
    }
  }
  ```

[hirer relationships]: /auth/hirer-relationships
[`hiringorganization`]: /schema/named-type/HiringOrganization
[`hiringorganization` query]: /schema/query/hiringOrganization
[`hiringorganizations` query]: /schema/query/hiringOrganizations
[link out applications]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form
[`seekanzadvertiser` query]: /schema/query/seekAnzAdvertiser
